import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  type MultiConnectionGroupItem,
  AnalyticsComponentProvider,
  AnalyticsScreenProvider,
  ConnectionGroup,
  Flex,
  Image,
  Link,
  Text,
  OnboardingLayout,
} from "@wayflyer/flyui";
import {
  IconArrowLeft24Line,
  IconShop24Line,
  IconUpload24Line,
  IconArrowRight24Line,
} from "@wayflyer/flyui-icons/24/line";
import { useMount, handleContactTeam } from "shared-utils";
import { type ReactNode, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import {
  AuthCallbackPath,
  CODAT_PLATFORM_LOGOS,
  CODAT_SUPPORTED_ACCOUNTING,
  ConnectionAuthCallbackHandler,
  ConnectionFlowsProvider,
  filteredAndSortedDataSourcesNext,
  isPrimaryRevenuePlatform,
  isSecondaryRevenuePlatform,
  OAUTH_ERROR_MESSAGES,
  useInviteModalActions,
  type ConnectionFlowsConfig,
} from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";
import { usePostHog } from "posthog-js/react";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";
import {
  ACCOUNTING_CONNECTOR_SLUGS,
  FUNDING_APPLICATION_CONNECTORS_API,
  MARKETING_CONNECTOR_SLUGS,
  ORDERED_CONNECTOR_SLUGS,
  REVENUE_CONNECTOR_SLUGS,
  getProviderType,
} from "../../../components/constants";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { usePlatformConnectors } from "../../../queries/connectors/queries";
import { getFundingApplicationStagesQueryOptions } from "../../../queries/funding-application-configuration/queries";
import { useFundingApplication, useFundingApplicationMutation } from "../../../queries/funding-application/queries";
import { getChecklistItemPropsForConnector } from "../../../utils/checklist-items";
import { filterConnectorsByStageConfig, getFilteredAccountingConnectors } from "../../../utils/connectors";
import { SkipPlatformSelectionModal } from "../../select-platforms/select-platforms-next/skip-platform-selection-modal";

import { CodatConnectionCallbackHandler } from "./codat-connection-callback-handler";
import { ConnectedPlatform } from "./connected-platform";
import styles from "./connection-manager.module.css";
import { MESSAGES } from "./message-definitions";
import { CombinedStatusBanner } from "../reconnection/combined-status-banner";
import { getOnlineSalesBannerState } from "../reconnection/online-sales-banner-state";
import { OnlineSalesStatusBanner } from "../reconnection/online-sales-status-banner";
import { getMarketingBannerState } from "../reconnection/marketing-banner-state";
import { MarketingStatusBanner } from "../reconnection/marketing-status-banner";

import { useDetectedBankAccountToast } from "./use-detected-bank-account-toast";

const CONNECTION_FLOWS_CONFIG: ConnectionFlowsConfig = {
  connectorsApiUrl: "/api/:customerSlug/funding/api/connectors/",
  completeConnectionApiUrl: "/api/:customerSlug/funding/api/connectors/complete-connection",
  completeConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/complete-connection",
  cancelConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/cancel-connection",
  defaultCallbackPath: AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION,
  supportsReconnection: true,
};

// Fuzzy-match identifierName (e.g. "Sage 50 (UK)") to a short platform name (e.g. "Sage").
function getCodatDisplayName(identifierName: string): string {
  const match = CODAT_SUPPORTED_ACCOUNTING.find((platform) =>
    identifierName.toLowerCase().startsWith(platform.toLowerCase())
  );
  return match ?? identifierName;
}

const ACCOUNTING_VISIBLE_COUNT = 6;

const PlatformConnectionButtons = () => {
  const intl = useIntl();
  const posthog = usePostHog();
  const { formatMessage } = intl;
  const [, setSearchParams] = useSearchParams();
  const [isPrimarySubmitting, setIsPrimarySubmitting] = useState(false);
  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.CONNECTORS });
  const applicationSubmit = useApplicationSubmit({ currentPage: PAGE_TYPE.CONNECTORS });
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const { data: connectorsWithConnections } = usePlatformConnectors(customerDomain);
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));

  const [isSkipRevenuePlatformSelected, setIsSkipRevenuePlatformSelected] = useState(
    fundingApplication.isEcommerceUnsupported ?? false
  );
  const [pendingSkipMarketingPlatform, setPendingSkipMarketingPlatform] = useState<boolean | null>(null);
  const isSkipMarketingPlatformSelected =
    pendingSkipMarketingPlatform ?? fundingApplication.isMarketingUnsupported ?? false;

  const { openInviteModal } = useInviteModalActions();

  useMount(() => {
    posthog?.capture("onboarding_connections_viewed");
  });

  const handleSkipRevenuePlatformChange = (value: boolean) => {
    const previousSkipState = isSkipRevenuePlatformSelected;
    setIsSkipRevenuePlatformSelected(value);
    fundingApplicationMutation.mutate(
      { id: fundingApplication.id, isEcommerceUnsupported: value },
      { onError: () => setIsSkipRevenuePlatformSelected(previousSkipState) }
    );
  };

  const handleSkipMarketingPlatformChange = (value: boolean) => {
    setPendingSkipMarketingPlatform(value);
    fundingApplicationMutation.mutate(
      { id: fundingApplication.id, isMarketingUnsupported: value },
      { onSettled: () => setPendingSkipMarketingPlatform(null) }
    );
  };

  const hasAccountingConnected = connectorsWithConnections.some(
    (c) => c.connections.length > 0 && ACCOUNTING_CONNECTOR_SLUGS.includes(c.slug)
  );

  const showRevenue = fundingApplicationStages?.revenue?.allowPlatformSelection;
  const showAccounting = fundingApplicationStages?.accounting?.allowPlatformSelection;
  const showMarketing = fundingApplicationStages?.marketing?.allowPlatformSelection;
  const hasRequiredRevenueSlug = !!fundingApplicationStages?.revenue?.requiredConnectorSlugs?.length;
  const hasRevenueConnected = connectorsWithConnections.some(
    (c) => c.connections.length > 0 && (isPrimaryRevenuePlatform(c.slug) || isSecondaryRevenuePlatform(c.slug))
  );
  const hasRequiredMarketingSlug = !!fundingApplicationStages?.marketing?.requiredConnectorSlugs?.length;
  const hasMarketingConnected = connectorsWithConnections.some(
    (c) => MARKETING_CONNECTOR_SLUGS.includes(c.slug) && c.connections.some((connection) => connection.isConnected)
  );

  const {
    eCommerceOptions,
    revenueConnectorsWithConnections,
    accountingOptions,
    marketingOptions,
    marketingConnectorsWithConnections,
  } = useMemo(() => {
    const filteredAndSortedDataSources = filteredAndSortedDataSourcesNext(connectorsWithConnections);
    const connectorsWithConnectionsMap = new Map(connectorsWithConnections.map((c) => [c.slug, c]));
    const connectedSlugsSet = new Set(
      connectorsWithConnections.filter((c) => c.connections.length > 0).map((c) => c.slug)
    );
    const openSlideout = ({ slug }: { slug: string }) => {
      posthog?.capture("onboarding_connection_selected", { provider: slug, provider_type: getProviderType(slug) });
      setSearchParams({ platform: slug });
    };

    const toChecklistItem = (connector: (typeof filteredAndSortedDataSources.shoppingDataSources)[number]) =>
      getChecklistItemPropsForConnector(
        connectorsWithConnectionsMap.get(connector.slug) ?? connector,
        connectedSlugsSet.has(connector.slug),
        openSlideout
      );

    const revenuePlatforms = filterConnectorsByStageConfig(
      filteredAndSortedDataSources.shoppingDataSources,
      fundingApplicationStages?.revenue
    ).filter((connector) => connector.slug !== "magento2");

    const accountingPlatforms = filterConnectorsByStageConfig(
      getFilteredAccountingConnectors(filteredAndSortedDataSources),
      fundingApplicationStages?.accounting
    );

    const marketingPlatforms = filterConnectorsByStageConfig(
      filteredAndSortedDataSources.marketingDataSources,
      fundingApplicationStages?.marketing
    );

    const hasRevenuePrimaryConnected = connectorsWithConnections.some(
      (c) => c.connections.length > 0 && isPrimaryRevenuePlatform(c.slug)
    );

    const revenueConnectorsWithConnections = revenuePlatforms.map(
      (connector) => connectorsWithConnectionsMap.get(connector.slug) ?? connector
    );

    const marketingConnectorsWithConnections = marketingPlatforms.map(
      (connector) => connectorsWithConnectionsMap.get(connector.slug) ?? connector
    );

    return {
      eCommerceOptions: revenueConnectorsWithConnections.map((connector) => {
        const isRequired = fundingApplicationStages?.revenue?.requiredConnectorSlugs?.includes(connector.slug);
        const isDisabled = !isRequired && isSecondaryRevenuePlatform(connector.slug) && hasRevenuePrimaryConnected;
        const hasError = connector.connections.some((connection) => !connection.isConnected);
        return getChecklistItemPropsForConnector(
          connector,
          connectedSlugsSet.has(connector.slug),
          openSlideout,
          false,
          isDisabled,
          hasError
        );
      }),
      revenueConnectorsWithConnections,
      accountingOptions: accountingPlatforms.map(toChecklistItem),
      marketingOptions: marketingConnectorsWithConnections.map((connector) => {
        const hasError = connector.connections.some((connection) => !connection.isConnected);
        return getChecklistItemPropsForConnector(
          connector,
          connectedSlugsSet.has(connector.slug),
          openSlideout,
          false,
          false,
          hasError
        );
      }),
      marketingConnectorsWithConnections,
    };
  }, [
    connectorsWithConnections,
    fundingApplicationStages?.revenue,
    fundingApplicationStages?.accounting,
    fundingApplicationStages?.marketing,
    posthog,
    setSearchParams,
  ]);

  const onlineSalesBannerState =
    showRevenue && eCommerceOptions.length > 0 ? getOnlineSalesBannerState(revenueConnectorsWithConnections) : null;
  const marketingBannerState =
    showMarketing && marketingOptions.length > 0 ? getMarketingBannerState(marketingConnectorsWithConnections) : null;
  const showCombinedBanner = !!onlineSalesBannerState && !!marketingBannerState;

  const codatConnector = connectorsWithConnections.find((c) => c.slug === "codat_accounting");
  const isCodatConnected = !!codatConnector && codatConnector.connections.length > 0;

  // One tile per known Codat platform. Unconnected tiles are disabled when any Codat connection exists.
  const codatKnownTiles: MultiConnectionGroupItem[] = CODAT_SUPPORTED_ACCOUNTING.map((platformName) => {
    const connectionCount =
      codatConnector?.connections.filter((c) => getCodatDisplayName(c.identifierName ?? "") === platformName).length ??
      0;
    const isConnected = connectionCount > 0;
    const logoSrc = CODAT_PLATFORM_LOGOS[platformName];
    return {
      checked: isConnected,
      label: platformName,
      logo: logoSrc ? <Image src={logoSrc} /> : <IconShop24Line />,
      name: `codat-${platformName}`,
      disabled: isCodatConnected && !isConnected,
      onChange: () => setSearchParams({ platform: "codat_accounting", codat_platform: platformName }),
      ...(isConnected && { connectionCount }),
    };
  });

  // A single tile for connections that didn't fuzzy-match a known platform (e.g. "Sandbox").
  const knownPlatformSet = new Set(CODAT_SUPPORTED_ACCOUNTING);
  const wildcardConnections =
    codatConnector?.connections.filter((c) => !knownPlatformSet.has(getCodatDisplayName(c.identifierName ?? ""))) ?? [];
  const codatWildcardTile: MultiConnectionGroupItem | null =
    wildcardConnections.length > 0
      ? {
          checked: true,
          label: getCodatDisplayName(wildcardConnections[0].identifierName ?? ""),
          logo: <IconShop24Line />,
          name: "codat-other",
          connectionCount: wildcardConnections.length,
          onChange: () => setSearchParams({ platform: "codat_accounting" }),
        }
      : null;

  const codatTiles = [...codatKnownTiles, ...(codatWildcardTile ? [codatWildcardTile] : [])];

  const uploadTileOption: MultiConnectionGroupItem | null =
    !hasAccountingConnected && !isCodatConnected
      ? {
          checked: !!fundingApplication.accountingDocumentsRequired,
          label: formatMessage(MESSAGES.uploadAccountingDocumentsLabel),
          name: "accounting-upload",
          logo: <IconUpload24Line />,
          onChange: () => setSearchParams({ platform: "accounting_upload" }),
        }
      : null;

  // Upload tile is always last. It renders in a separate group but participates in show-more logic.
  const allAccountingOptions = [...accountingOptions, ...codatTiles, ...(uploadTileOption ? [uploadTileOption] : [])];

  // Start expanded if a checked tile would fall beyond the visible window.
  const [isAccountingExpanded, setIsAccountingExpanded] = useState(() =>
    allAccountingOptions.slice(ACCOUNTING_VISIBLE_COUNT).some((o) => o.checked)
  );

  const visibleAccountingOptions: MultiConnectionGroupItem[] = (() => {
    if (isAccountingExpanded) {
      return allAccountingOptions;
    }
    const uncheckedItems = allAccountingOptions.filter((o) => !o.checked);
    const hiddenUnchecked = uncheckedItems.slice(ACCOUNTING_VISIBLE_COUNT);
    if (hiddenUnchecked.length === 0) {
      return allAccountingOptions;
    }
    const visibleNames = new Set(uncheckedItems.slice(0, ACCOUNTING_VISIBLE_COUNT).map((o) => o.name));
    return [
      ...allAccountingOptions.filter((o) => o.checked || visibleNames.has(o.name)),
      {
        checked: false,
        label: formatMessage(MESSAGES.viewMoreLabel),
        logo: (
          <Text size="lg" fontWeight="semiBold" color="placeholder">
            +{hiddenUnchecked.length}
          </Text>
        ),
        name: "show-more-accounting",
        onChange: () => setIsAccountingExpanded(true),
      },
    ];
  })();

  const handleNext = async () => {
    const connectedSlugs = connectorsWithConnections
      .filter((c) => c.connections.length > 0 && ORDERED_CONNECTOR_SLUGS.includes(c.slug))
      .map((c) => c.slug);
    const currentSlugs = fundingApplication.selectedConnectorSlugs ?? [];
    const missingSlugs = connectedSlugs.filter((slug) => !currentSlugs.includes(slug));
    setIsPrimarySubmitting(true);
    try {
      // Current stage satisfaction criteria requires connection slugs attached to funding application
      if (missingSlugs.length > 0) {
        await fundingApplicationMutation.mutateAsync({
          id: fundingApplication.id,
          selectedConnectorSlugs: [...currentSlugs, ...missingSlugs],
        });
      }
      if (!navigationContext.nextPath) {
        applicationSubmit.submitApplication();
      } else {
        await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
      }
    } finally {
      setIsPrimarySubmitting(false);
    }
  };

  const footer = (
    <Flex direction="column" gap="2">
      <Text size="xs" fontWeight="semiBold">
        {formatMessage(MESSAGES.anyDoubtsHeading)}
      </Text>
      {formatMessage(MESSAGES.helpFooter, {
        p: (chunks: ReactNode) => (
          <Text color="placeholder" size="xs">
            {chunks}
          </Text>
        ),
        a1: (chunks: ReactNode) => <Link onClick={() => openInviteModal()}>{chunks}</Link>,
        a2: (chunks: ReactNode) => <Link onClick={handleContactTeam}>{chunks}</Link>,
      })}
    </Flex>
  );

  return (
    <AnalyticsScreenProvider screen="onboarding-platform-selector">
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        rowGap="8"
        title={formatMessage(MESSAGES.pageTitle)}
        subtitle={formatMessage(MESSAGES.pageDescription)}
        footer={<AnalyticsComponentProvider component="connection-help-footer">{footer}</AnalyticsComponentProvider>}
        actions={{
          primary: {
            content: navigationContext.nextButtonLabel,
            loading: isPrimarySubmitting || applicationSubmit.isSubmitting,
            onAction: handleNext,
            icon: IconArrowRight24Line,
            analyticsId: "connection-manager-next-button",
            testId: "navigation-continue-button",
          },
          secondary: {
            content: navigationContext.backButtonLabel,
            icon: IconArrowLeft24Line,
            onAction: () => {
              if (navigationContext.previousPath) {
                navigationContext.navigate(navigationContext.previousPath);
              }
            },
            analyticsId: "connection-manager-back-button",
            testId: "navigation-back-button",
          },
        }}
      >
        {showCombinedBanner && <CombinedStatusBanner blocking={onlineSalesBannerState?.variant === "critical"} />}
        {!showCombinedBanner && onlineSalesBannerState && <OnlineSalesStatusBanner state={onlineSalesBannerState} />}
        {!showCombinedBanner && marketingBannerState && <MarketingStatusBanner state={marketingBannerState} />}

        {showRevenue && eCommerceOptions.length > 0 && (
          <Flex className={styles.connectionGroup} direction="column" gap="6" justify="flex-start">
            <ConnectionGroup
              selectionMode="multi"
              isOptional={!fundingApplicationStages?.revenue?.platformRequired}
              categoryLabel={formatMessage(MESSAGES.onlineSalesHeading)}
              options={eCommerceOptions}
              selectedUnsupportedPlatforms={[]}
              unsupportedOptions={[]}
              addUnsupportedPlatform={() => {}}
              skipPlatformProps={{
                onChange: handleSkipRevenuePlatformChange,
                disabled: hasRequiredRevenueSlug || hasRevenueConnected,
                selected: isSkipRevenuePlatformSelected,
              }}
              SkipPlatformSelectionComponent={SkipPlatformSelectionModal}
              renderConnectionCount={(count: number, hasError?: boolean) =>
                hasError && count === 1
                  ? intl.formatMessage(MESSAGES.onlineSalesDisconnectedSubtitle)
                  : intl.formatMessage(MESSAGES.connectionCount, { count })
              }
            />
          </Flex>
        )}

        {showAccounting && allAccountingOptions.length > 0 && (
          <div className={styles.connectionGroup}>
            <ConnectionGroup
              selectionMode="multi"
              isOptional={!fundingApplicationStages?.accounting?.platformRequired}
              categoryLabel={formatMessage(MESSAGES.accountingHeading)}
              options={visibleAccountingOptions}
              selectedUnsupportedPlatforms={[]}
              unsupportedOptions={[]}
              addUnsupportedPlatform={() => {}}
              renderConnectionCount={(count: number) => intl.formatMessage(MESSAGES.uploadFileCount, { count })}
            />
          </div>
        )}

        {showMarketing && marketingOptions.length > 0 && (
          <Flex className={styles.connectionGroup} direction="column" gap="6" justify="flex-start">
            <ConnectionGroup
              selectionMode="multi"
              isOptional={!fundingApplicationStages?.marketing?.platformRequired}
              categoryLabel={formatMessage(MESSAGES.marketingHeading)}
              options={marketingOptions}
              selectedUnsupportedPlatforms={[]}
              unsupportedOptions={[]}
              addUnsupportedPlatform={() => {}}
              skipPlatformProps={{
                onChange: handleSkipMarketingPlatformChange,
                disabled: hasRequiredMarketingSlug || hasMarketingConnected,
                selected: isSkipMarketingPlatformSelected,
              }}
              SkipPlatformSelectionComponent={SkipPlatformSelectionModal}
              renderConnectionCount={(count: number, hasError?: boolean) =>
                hasError && count === 1
                  ? intl.formatMessage(MESSAGES.marketingDisconnectedSubtitle)
                  : intl.formatMessage(MESSAGES.connectionCount, { count })
              }
            />
          </Flex>
        )}
      </OnboardingLayout>
    </AnalyticsScreenProvider>
  );
};

export const ConnectionManagerNext = () => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const showDetectedBankAccountToast = useDetectedBankAccountToast();

  const handleConnectionSuccess = (searchParams: URLSearchParams) => {
    const connectionSucceedSlug = searchParams.get("connectionSucceed");
    const oAuthError = searchParams.get("oauth-error");

    if (!!oAuthError || !connectionSucceedSlug) {
      return;
    }

    // Completing in the side panel no longer reloads the page, so refresh the connector lists here.
    void queryClient.invalidateQueries({
      queryKey: [FUNDING_APPLICATION_CONNECTORS_API.replace(":customerSlug", customerDomain)],
    });

    posthog?.capture("onboarding_connection_successful", {
      provider: connectionSucceedSlug,
      provider_type: getProviderType(connectionSucceedSlug),
    });

    void showDetectedBankAccountToast(connectionSucceedSlug);

    const currentSlugs = fundingApplication.selectedConnectorSlugs ?? [];

    fundingApplicationMutation.mutate({
      id: fundingApplication.id,
      selectedConnectorSlugs: currentSlugs.includes(connectionSucceedSlug)
        ? undefined
        : [...currentSlugs, connectionSucceedSlug],
      isEcommerceUnsupported: REVENUE_CONNECTOR_SLUGS.includes(connectionSucceedSlug) ? false : undefined,
      isMarketingUnsupported: MARKETING_CONNECTOR_SLUGS.includes(connectionSucceedSlug) ? false : undefined,
      accountingDocumentsRequired: ACCOUNTING_CONNECTOR_SLUGS.includes(connectionSucceedSlug) ? false : undefined,
    });
  };

  const handleConnectionError = (searchParams: URLSearchParams) => {
    const platform = searchParams.get("platform");
    const oAuthError = searchParams.get("oauth-error");
    if (platform && oAuthError) {
      const normalizedError = oAuthError in OAUTH_ERROR_MESSAGES ? oAuthError : "unknown";
      posthog?.capture("onboarding_connection_failed", {
        provider: platform,
        provider_type: getProviderType(platform),
        error: normalizedError,
      });
    }
  };

  return (
    <ConnectionFlowsProvider config={CONNECTION_FLOWS_CONFIG}>
      <ConnectionAuthCallbackHandler onSuccess={handleConnectionSuccess} onError={handleConnectionError}>
        <CodatConnectionCallbackHandler />
        <PlatformConnectionButtons />
        <ConnectedPlatform />
      </ConnectionAuthCallbackHandler>
    </ConnectionFlowsProvider>
  );
};
